import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {HoverLabelTextField} from '@/components/common';
import * as CompanyGLCategoryService from '@/services/accountService/companyGLCategoryService';
import * as GlCategoryService from '@/services/accountService/glCategoryService';
import {stripDupSuffix} from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import {
    defaultOpenIdsForLevels,
    flattenTree,
    getVisibleTreeRows,
    treeLeadingCellSx,
} from '@/utils/treeTableHelpers';
import {TreeExpandCell} from '@/utils/TreeExpandCell';

interface CompanyGLEditorPreviewProps {
    companyId: number;
    companyName?: string;
}

/** 截断文本；有内容时悬停显示全文 */
const TruncateWithTooltip: React.FC<{
    value?: string;
    emptyPlaceholder?: string;
    fontWeight?: number | string;
    /** 客户别名列着色 */
    accent?: boolean;
}> = ({value, emptyPlaceholder = '—', fontWeight, accent}) => {
    const text = value?.trim() ? value : emptyPlaceholder;
    if (!value?.trim()) {
        return (
            <Typography variant="body2" color="text.disabled" noWrap sx={{fontWeight}}>
                {text}
            </Typography>
        );
    }
    return (
        <Tooltip title={value} enterDelay={300}>
            <Typography
                variant="body2"
                noWrap
                sx={{
                    cursor: 'default',
                    fontWeight: accent ? 500 : fontWeight,
                    color: accent ? 'info.main' : undefined,
                }}
            >
                {value}
            </Typography>
        </Tooltip>
    );
};

type PreviewNode = {
    /** 树展开用稳定 id：标准科目用 categoryId；别名行用负 accountId */
    nodeId: number;
    categoryId?: number;
    code?: string;
    nameEn?: string;
    nameZh?: string;
    type?: string;
    /** 别名叶子（挂在标准叶子下） */
    isAliasRow?: boolean;
    aliasEn?: string;
    aliasZh?: string;
    normalSide?: string;
    currency?: string;
    children?: PreviewNode[];
};

const typeColor = (t?: string) => {
    switch (t) {
        case 'ASSET':
            return 'success';
        case 'LIABILITY':
            return 'warning';
        case 'EQUITY':
            return 'info';
        case 'REVENUE':
            return 'secondary';
        case 'EXPENSE':
            return 'error';
        case 'ADJUSTMENT':
            return 'default';
        default:
            return 'default';
    }
};

function normalizeSide(side?: string | null): string {
    const s = String(side || '').trim().toUpperCase();
    return s === 'DR' || s === 'CR' ? s : (side || '');
}

function parseTreeResponse(res: unknown): GlCategoryService.GlCategory[] {
    if (Array.isArray(res)) return res as GlCategoryService.GlCategory[];
    if (res && typeof res === 'object') {
        const o = res as {items?: GlCategoryService.GlCategory[]; list?: GlCategoryService.GlCategory[]};
        if (Array.isArray(o.items)) return o.items;
        if (Array.isArray(o.list)) return o.list;
    }
    return [];
}

/** 标准叶子 code → 该公司别名（含 -DUP 变体） */
function groupAliasesByBaseCode(
    aliases: CompanyGLCategoryService.GlAccount[],
): Map<string, CompanyGLCategoryService.GlAccount[]> {
    const m = new Map<string, CompanyGLCategoryService.GlAccount[]>();
    for (const a of aliases) {
        const raw = String(a.code || '').trim();
        if (!raw) continue;
        const base = stripDupSuffix(raw) || raw;
        const list = m.get(base) ?? [];
        list.push(a);
        m.set(base, list);
    }
    for (const [, list] of m) {
        list.sort((x, y) => String(x.code || '').localeCompare(String(y.code || '')));
    }
    return m;
}

/**
 * 把标准 GL 树裁成「有公司别名」的路径，并在叶子上挂别名。
 * - 1 个别名：写在标准叶子行上
 * - 多个别名（含 DUP）：标准叶子下挂别名子行
 */
function buildPreviewTree(
    roots: GlCategoryService.GlCategory[],
    byBase: Map<string, CompanyGLCategoryService.GlAccount[]>,
): PreviewNode[] {
    const walk = (nodes: GlCategoryService.GlCategory[] | undefined): PreviewNode[] => {
        if (!nodes?.length) return [];
        const out: PreviewNode[] = [];
        for (const n of nodes) {
            const id = n.categoryId;
            if (id == null) continue;
            const childNodes = walk(n.children as GlCategoryService.GlCategory[] | undefined);
            const code = String(n.code || '').trim();
            const aliases = code ? (byBase.get(code) ?? []) : [];
            const isLeaf = !n.children?.length;

            if (!isLeaf) {
                if (childNodes.length === 0) continue;
                out.push({
                    nodeId: id,
                    categoryId: id,
                    code: n.code,
                    nameEn: n.nameEn,
                    nameZh: n.nameZh,
                    type: n.type,
                    children: childNodes,
                });
                continue;
            }

            if (aliases.length === 0) continue;

            if (aliases.length === 1) {
                const a = aliases[0];
                out.push({
                    nodeId: id,
                    categoryId: id,
                    code: a.code || n.code,
                    nameEn: n.nameEn,
                    nameZh: n.nameZh,
                    type: n.type,
                    aliasEn: a.aliasEn,
                    aliasZh: a.aliasZh,
                    normalSide: normalizeSide(a.normalSide),
                    currency: a.currency,
                });
            } else {
                out.push({
                    nodeId: id,
                    categoryId: id,
                    code: n.code,
                    nameEn: n.nameEn,
                    nameZh: n.nameZh,
                    type: n.type,
                    children: aliases.map((a, idx) => ({
                        nodeId: a.accountId != null
                            ? -Number(a.accountId)
                            : -(id * 1000 + idx + 1),
                        code: a.code,
                        nameEn: n.nameEn,
                        nameZh: n.nameZh,
                        type: n.type,
                        isAliasRow: true,
                        aliasEn: a.aliasEn,
                        aliasZh: a.aliasZh,
                        normalSide: normalizeSide(a.normalSide),
                        currency: a.currency,
                    })),
                });
            }
        }
        return out;
    };
    return walk(roots);
}

/**
 * 公司 GL 别名映射只读预览：标准 GL 层级树 + 叶子上的公司别名。
 * 样式对齐 {@link GlCategoryList}。
 */
const CompanyGLEditorPreview: React.FC<CompanyGLEditorPreviewProps> = ({companyId, companyName}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [aliasCount, setAliasCount] = useState(0);
    const [tree, setTree] = useState<PreviewNode[]>([]);
    const [openRowIds, setOpenRowIds] = useState<number[]>([]);
    /** 默认显示客户别名列；可隐藏以只看标准 GL 名称 */
    const [showAliasColumns, setShowAliasColumns] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!companyId) return;
            setLoading(true);
            setError(null);
            try {
                const [aliasList, treeRes] = await Promise.all([
                    CompanyGLCategoryService.listAccountsByCompany(Number(companyId)),
                    GlCategoryService.getCategoryTree({}),
                ]);
                if (cancelled) return;
                const aliases = aliasList || [];
                setAliasCount(aliases.length);
                const roots = parseTreeResponse(treeRes);
                const byBase = groupAliasesByBaseCode(aliases);
                const preview = buildPreviewTree(roots, byBase);
                setTree(preview);
                // 默认展开到二级（Assets → Current Assets），叶子可见
                setOpenRowIds(defaultOpenIdsForLevels(preview, (n) => n.nodeId, 2));
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || 'Load failed');
                    setTree([]);
                    setAliasCount(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    const flat = useMemo(() => flattenTree(tree, (n) => n.nodeId), [tree]);
    const visible = useMemo(() => getVisibleTreeRows(flat, openRowIds), [flat, openRowIds]);

    const handleToggleRow = (id: number) => {
        setOpenRowIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };

    return (
        <Box sx={{p: 2}}>
            <Stack spacing={2}>
                <Paper sx={{p: 2}}>
                    <HoverLabelTextField
                        label="Company"
                        size="small"
                        fullWidth
                        value={companyName || String(companyId)}
                        disabled
                        helperText={' '}
                    />
                </Paper>

                <Paper sx={{p: 2, display: 'flex', flexDirection: 'column', minHeight: 360}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Alias Mappings ({aliasCount})
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={showAliasColumns}
                                        onChange={(e) => setShowAliasColumns(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="caption">Show client alias columns</Typography>}
                                sx={{mr: 0}}
                            />
                            <Typography variant="caption" color="text.secondary">
                                Tree follows standard GL Category · only branches with aliases
                            </Typography>
                        </Stack>
                    </Stack>
                    {loading && <CircularProgress size={20} sx={{my: 1}}/>}
                    {error && (
                        <Typography color="error" variant="body2" sx={{mb: 1}}>{error}</Typography>
                    )}
                    <TableContainer sx={{flex: 1, maxHeight: 520, overflow: 'auto'}}>
                        <Table
                            size="small"
                            stickyHeader
                            sx={{minWidth: showAliasColumns ? 900 : 700, tableLayout: 'fixed'}}
                        >
                            <TableHead>
                                <TableRow sx={{background: theme.palette.action.hover}}>
                                    <TableCell sx={{width: 48, p: 0}}/>
                                    <TableCell sx={{width: 150, fontWeight: 700}}>Code</TableCell>
                                    <TableCell sx={{width: 200, fontWeight: 700}}>Name (EN)</TableCell>
                                    <TableCell sx={{width: 160, fontWeight: 700}}>Name (ZH)</TableCell>
                                    <TableCell sx={{width: 100, fontWeight: 700}}>Type</TableCell>
                                    {showAliasColumns && (
                                        <TableCell sx={{fontWeight: 700, color: 'info.main'}}>Alias EN</TableCell>
                                    )}
                                    {showAliasColumns && (
                                        <TableCell sx={{fontWeight: 700, color: 'info.main'}}>Alias ZH</TableCell>
                                    )}
                                    <TableCell sx={{width: 72, fontWeight: 700}} align="center">Side</TableCell>
                                    <TableCell sx={{width: 64, fontWeight: 700}} align="center">CCY</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && visible.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={showAliasColumns ? 9 : 7} align="center">
                                            No alias mappings
                                        </TableCell>
                                    </TableRow>
                                )}
                                {visible.map((r) => {
                                    const hasChildren = !!(r.children && r.children.length > 0);
                                    const isGroup = hasChildren && !r.isAliasRow && !r.aliasEn;
                                    return (
                                        <TableRow
                                            key={r.nodeId}
                                            hover
                                            sx={{
                                                bgcolor: r.isAliasRow
                                                    ? 'action.hover'
                                                    : isGroup
                                                        ? undefined
                                                        : undefined,
                                                '& > *': {borderBottom: 'unset'},
                                            }}
                                        >
                                            <TableCell sx={{width: 48, p: 0.5, ...treeLeadingCellSx(r.level)}}>
                                                <TreeExpandCell
                                                    hasChildren={hasChildren}
                                                    isOpen={openRowIds.includes(r.nodeId)}
                                                    onToggle={() => handleToggleRow(r.nodeId)}
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...treeLeadingCellSx(r.level),
                                                    fontWeight: isGroup ? 600 : 400,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {r.code || '—'}
                                            </TableCell>
                                            <TableCell sx={{overflow: 'hidden'}}>
                                                <TruncateWithTooltip
                                                    value={r.nameEn}
                                                    fontWeight={isGroup ? 600 : 500}
                                                />
                                            </TableCell>
                                            <TableCell sx={{overflow: 'hidden'}}>
                                                <TruncateWithTooltip value={r.nameZh}/>
                                            </TableCell>
                                            <TableCell>
                                                {r.type
                                                    ? <Chip size="small" color={typeColor(r.type) as any} label={r.type}/>
                                                    : '—'}
                                            </TableCell>
                                            {showAliasColumns && (
                                                <TableCell sx={{overflow: 'hidden'}}>
                                                    <TruncateWithTooltip
                                                        value={r.aliasEn}
                                                        emptyPlaceholder={isGroup ? '' : '—'}
                                                        accent
                                                    />
                                                </TableCell>
                                            )}
                                            {showAliasColumns && (
                                                <TableCell sx={{overflow: 'hidden'}}>
                                                    <TruncateWithTooltip
                                                        value={r.aliasZh}
                                                        emptyPlaceholder={isGroup ? '' : '—'}
                                                        accent
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell align="center">
                                                {r.normalSide || (isGroup ? '' : '—')}
                                            </TableCell>
                                            <TableCell align="center">
                                                {r.currency || (isGroup ? '' : '—')}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Stack>
        </Box>
    );
};

export default CompanyGLEditorPreview;
